<template>
  <GeneralDialog v-model:visible="visible">
    <div class="min-w-[250px] p-3.75">
      <div class="mb-7.5">
        <div :class="$typo.openSansBold16" class="leading-3 text-text-muted mb-4">
          {{ headerLabelTranslated }}
        </div>
      </div>
      <div
        v-for="option in options"
        :key="option.value"
        class="flex items-center mb-5"
        :class="[
          option.disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer',
        ]"
        @click="() => onValueSelected(option.value)"
      >
        <div class="w-3 h-3 rounded-full border border-[var(--knob-color)] mr-2 relative">
          <div
            v-if="option.value === value"
            class="absolute left-0.5 top-0.5 rounded-full w-1.5 h-1.5 bg-[var(--knob-color)]"
          ></div>
        </div>
        <div :class="$typo.openSans14" class="text-text leading-3.5">
          {{ option.translatedLabel ?? t(option.label) }}
        </div>
      </div>
      <div class="flex justify-between">
        <div></div>
        <div class="flex gap-2.5">
          <Button @click="close">
            {{ t('optionsModalCancel') }}
          </Button>
          <Button @click="saveAndClose">
            {{ t('optionsModalOkay') }}
          </Button>
        </div>
      </div>
    </div>
  </GeneralDialog>
</template>

<script setup lang="ts" generic="T">
import { computed, ref, toRefs } from 'vue'
import { TranslationKey, TranslationUnit, useLocalizationStore } from '@/stores/localization.ts'
import $typo from '@/styles/typografy.module.css'
import GeneralDialog from '@/components/GeneralDialog.vue'
import Button from '@/components/Button.vue'
import { Option } from '@/stores/ui.store.ts'

const props = defineProps<{
  options: Option<T>[]
  selectedValue: T | null
  headerLabel: TranslationKey | TranslationUnit
}>()

const { options, headerLabel, selectedValue } = toRefs(props)

const headerLabelTranslated = computed(() => t(headerLabel.value))

const visible = defineModel('visible', { default: false })

const emit = defineEmits<{
  (e: 'save', value: T | null): void
}>()

const { t } = useLocalizationStore()
const _tmpValue = ref<null | T | undefined>(undefined)

const value = computed(() => {
  if (_tmpValue.value === undefined) {
    return selectedValue.value
  }
  return _tmpValue.value
})

function onValueSelected(newValue: T) {
  _tmpValue.value = newValue
}

function close() {
  visible.value = false
}

function saveAndClose() {
  emit('save', value.value)
  _tmpValue.value = undefined
  close()
}
</script>
